import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function WealthBuildingPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Wealth Building | Life Insurance & Financial Planning';

    const setMeta = (name: string, content: string, prop = false) => {
      const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        prop ? el.setAttribute('property', name) : el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Build lasting wealth with strategic life insurance planning. Whole life, IUL & annuities create tax-advantaged wealth for Indiana families.');
    setMeta('keywords', 'wealth building, life insurance wealth, whole life cash value, indexed universal life, financial planning Indiana');
    setMeta('last-modified', new Date().toISOString());
    
    setMeta('og:title', 'Wealth Building Strategies | Life Insurance & Financial Planning', true);
    setMeta('og:description', 'Build lasting wealth with strategic life insurance planning in Indiana. Tax-advantaged strategies for growing families.', true);
    setMeta('og:url', `${SITE_URL}/wealth-building`, true);
    setMeta('og:type', 'website', true);
    setMeta('og:site_name', 'Hoosier Life Insurance', true);
    
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'Wealth Building Strategies | Life Insurance & Financial Planning');
    setMeta('twitter:description', 'Build lasting wealth with strategic life insurance planning in Indiana.');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_URL}/wealth-building`);
  }, []);

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/wealth-building#webpage`,
        url: `${SITE_URL}/wealth-building`,
        name: 'Wealth Building Strategies | Hoosier Life Insurance',
        description: 'Build generational wealth with strategic life insurance planning. Discover how permanent life insurance can create tax-advantaged growth and legacy protection for Indiana families.',
        isPartOf: {
          '@id': `${SITE_URL}/#website`
        },
        breadcrumb: {
          '@id': `${SITE_URL}/wealth-building#breadcrumb`
        }
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${SITE_URL}/wealth-building#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': SITE_URL,
              name: 'Home'
            }
          },
          {
            '@type': 'ListItem',
            position: 2,
            item: {
              '@id': `${SITE_URL}/wealth-building`,
              name: 'Wealth Building'
            }
          }
        ]
      },
      {
        '@type': 'Service',
        '@id': `${SITE_URL}/wealth-building#service`,
        name: 'Wealth Building Strategies',
        description: 'Strategic life insurance planning for wealth accumulation, tax-advantaged growth, and generational wealth transfer for Indiana families.',
        provider: {
          '@type': 'InsuranceAgency',
          name: 'Hoosier Life Insurance',
          url: SITE_URL,
          telephone: '+1-317-316-4094',
          areaServed: {
            '@type': 'State',
            name: 'Indiana'
          }
        },
        areaServed: {
          '@type': 'State',
          name: 'Indiana'
        },
        serviceType: 'Wealth Building & Life Insurance Planning',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          availableDeliveryMethod: ['Phone', 'Video Conference', 'In-Home Consultation']
        }
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/wealth-building#faq`,
        mainEntity: [
          {
            '@type': 'Question',
            name: 'How can life insurance help build wealth?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Permanent life insurance policies like whole life and indexed universal life accumulate cash value that grows tax-deferred. You can access this cash value through loans or withdrawals for major expenses, retirement income, or investment opportunities while maintaining your death benefit protection.'
            }
          },
          {
            '@type': 'Question',
            name: 'What is the difference between term life and wealth-building life insurance?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Term life insurance provides pure death benefit protection for a specific period (10-30 years) with no cash value. Wealth-building policies like whole life or indexed universal life combine death benefit protection with a cash value component that grows over time and can be used as a financial asset during your lifetime.'
            }
          },
          {
            '@type': 'Question',
            name: 'Is life insurance cash value growth really tax-free?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Cash value growth inside a life insurance policy is tax-deferred, meaning you don\'t pay taxes on gains as they accumulate. Policy loans are generally tax-free as long as the policy remains in force. Death benefits paid to beneficiaries are also typically income tax-free.'
            }
          },
          {
            '@type': 'Question',
            name: 'How much should I invest in a wealth-building life insurance policy?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'The optimal amount depends on your income, financial goals, and overall wealth strategy. Generally, wealth-building policies work best when you can commit to consistent premiums for at least 10-15 years. We recommend a personalized analysis to determine the right coverage amount and premium structure for your situation.'
            }
          }
        ]
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-orange-600 transition-colors">
                Home
              </Link>
            </li>
            <li className="flex items-center">
              <i className="ri-arrow-right-s-line text-gray-400 text-lg"></i>
              <span className="text-orange-600 font-medium ml-2">Wealth Building</span>
            </li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Strategic Life Insurance for Wealth Building — Indianapolis, Indiana
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-8 leading-relaxed">
              Discover how life insurance creates tax-advantaged wealth, protects your family, and builds a financial legacy for Indiana families.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/wealth-strategy-call"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Book Free Strategy Call
                <i className="ri-arrow-right-line ml-2 text-xl"></i>
              </Link>
              <Link
                to="/book-appointment"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-slate-900 font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Get Your Free Quote
                <i className="ri-calculator-line ml-2 text-xl"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Life Insurance for Wealth Building */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Use Life Insurance for Wealth Building?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Life insurance isn't just about death benefits — it's a powerful wealth-building tool that offers unique tax advantages and guarantees you won't find in traditional investments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Tax-Free Death Benefit</h3>
              <p className="text-gray-700 leading-relaxed">
                Life insurance death benefits pass to your beneficiaries 100% income tax-free, making it one of the most tax-efficient wealth transfer tools available.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-line-chart-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Tax-Deferred Growth</h3>
              <p className="text-gray-700 leading-relaxed">
                Cash value inside permanent life insurance policies grows tax-deferred. You don't pay taxes on gains until you withdraw them — and you can access funds tax-free through policy loans.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-lock-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Creditor Protection</h3>
              <p className="text-gray-700 leading-relaxed">
                In Indiana, life insurance cash value and death benefits are protected from creditors, lawsuits, and bankruptcy — ensuring your wealth reaches your family.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-funds-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Guaranteed Growth</h3>
              <p className="text-gray-700 leading-relaxed">
                <Link to="/whole-life" className="text-orange-600 hover:underline font-medium">Whole life insurance with cash value</Link> offers guaranteed growth regardless of market conditions. Your wealth grows steadily and predictably year after year.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-hand-coin-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Access to Cash Value</h3>
              <p className="text-gray-700 leading-relaxed">
                Borrow against your policy's cash value tax-free for emergencies, opportunities, or retirement income — without credit checks or approval delays.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center mb-6">
                <i className="ri-shield-star-line text-orange-600 text-3xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">No Market Risk</h3>
              <p className="text-gray-700 leading-relaxed">
                Unlike stocks or mutual funds, permanent life insurance cash value never loses money due to market downturns. Your principal is always protected. Pair this with <Link to="/annuities" className="text-orange-600 hover:underline font-medium">guaranteed growth annuities</Link> for maximum security.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mid-Page Quiz CTA */}
      <section className="py-12 bg-orange-50 border-y border-orange-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-orange-600 uppercase tracking-wide mb-2">Personalized for You</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            See How Our 6-Step System Works for YOUR Family
          </h2>
          <p className="text-gray-700 mb-6">
            Answer 5 quick questions and get a customized wealth-building roadmap based on your age, income, and goals.
          </p>
          <Link
            to="/quiz"
            className="inline-flex items-center justify-center px-8 py-4 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
          >
            Take the Free Quiz
            <i className="ri-arrow-right-line ml-2 text-xl"></i>
          </Link>
        </div>
      </section>

      {/* Wealth Building Strategies */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Three Wealth-Building Strategies Using Life Insurance
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Indiana families use these proven strategies to build wealth, reduce taxes, and create financial security.
            </p>
          </div>

          <div className="space-y-12">
            {/* Strategy 1 */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-orange-600">1</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">The Infinite Banking Strategy</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Use a high-cash-value <Link to="/whole-life" className="text-orange-600 hover:underline font-medium">whole life insurance with cash value</Link> as your own personal bank. Borrow against your cash value tax-free to fund business opportunities, real estate investments, or major purchases — while your cash value continues to grow.
                  </p>
                  <div className="bg-orange-50 border-l-4 border-orange-600 p-4 rounded">
                    <p className="text-sm text-gray-800">
                      <strong>Example:</strong> A 35-year-old Indiana business owner funds a $50,000/year whole life policy. After 10 years, they have $400,000+ in cash value. They borrow $100,000 tax-free to expand their business while the policy continues earning dividends on the full cash value.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Strategy 2 */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-orange-600">2</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">The Retirement Income Strategy</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Build cash value in an <Link to="/indexed-universal-life" className="text-orange-600 hover:underline font-medium">indexed universal life insurance</Link> (IUL) policy during your working years, then take tax-free policy loans in retirement to supplement Social Security and 401(k) withdrawals — without increasing your taxable income. You can also pair this with <Link to="/annuities" className="text-orange-600 hover:underline font-medium">fixed indexed annuities</Link> for guaranteed retirement income with zero market risk.
                  </p>
                  <div className="bg-orange-50 border-l-4 border-orange-600 p-4 rounded">
                    <p className="text-sm text-gray-800">
                      <strong>Example:</strong> A 40-year-old Indianapolis professional contributes $1,000/month to an IUL policy. At age 65, they have $800,000+ in cash value. They take $50,000/year in tax-free loans for 20+ years, preserving their 401(k) and avoiding higher tax brackets.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Strategy 3 */}
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-orange-600">3</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">The Legacy Wealth Transfer Strategy</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Combine term life for income replacement with whole life for permanent wealth transfer. Your term policy protects your family during working years, while your whole life policy builds cash value and guarantees a tax-free inheritance. Want to <Link to="/generational-wealth" className="text-orange-600 hover:underline font-medium">build generational wealth for your family</Link>? This strategy is the foundation.
                  </p>
                  <div className="bg-orange-50 border-l-4 border-orange-600 p-4 rounded">
                    <p className="text-sm text-gray-800">
                      <strong>Example:</strong> A Carmel family carries $1 million in term life for mortgage and income protection, plus a $250,000 whole life policy. The term expires at age 65, but the whole life policy continues for life — leaving a guaranteed $250,000+ tax-free inheritance to their children.
                    </p>
                  </div>
                  <div className="mt-4">
                    <Link
                      to="/term-vs-whole-life"
                      className="inline-flex items-center text-orange-600 hover:text-orange-700 font-medium text-sm"
                    >
                      <i className="ri-scales-line mr-2"></i>
                      Term vs. whole life — which builds more wealth? Read the comparison →
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison: Life Insurance vs Traditional Investments */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Life Insurance vs. Traditional Investments
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              How does life insurance stack up against stocks, bonds, and real estate for wealth building?
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-lg overflow-hidden">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-6 py-4 text-left">Feature</th>
                  <th className="px-6 py-4 text-center">Life Insurance</th>
                  <th className="px-6 py-4 text-center">Stocks/Mutual Funds</th>
                  <th className="px-6 py-4 text-center">Real Estate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-6 py-4 font-semibold">Tax-Free Growth</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Yes</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ Taxed annually</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ Taxed on gains</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Guaranteed Returns</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Yes (Whole Life)</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ Market risk</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ Market dependent</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold">Creditor Protection</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Yes (Indiana)</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ No</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ No</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Death Benefit</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Tax-free payout</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ No death benefit</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ No death benefit</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-semibold">Liquidity</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Policy loans</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ Sell anytime</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ Slow to sell</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold">Downside Protection</td>
                  <td className="px-6 py-4 text-center text-green-600 font-bold">✓ No losses</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ Can lose value</td>
                  <td className="px-6 py-4 text-center text-red-600">✗ Can lose value</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 bg-orange-50 border-l-4 border-orange-600 p-6 rounded-lg">
            <p className="text-lg text-gray-800 leading-relaxed">
              <strong className="text-slate-900">Bottom line:</strong> Life insurance isn't a replacement for traditional investments — it's a complement. The best wealth-building strategy uses a mix of life insurance, retirement accounts, and real estate to maximize tax advantages and minimize risk.
            </p>
          </div>
        </div>
      </section>

      {/* Products That Power the 6-Step Wealth-Building System */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Products That Power the 6-Step Wealth-Building System
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Every step of the system is backed by a specific financial product. Here's how they work together.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to="/whole-life"
              className="group bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-200 hover:border-orange-400 flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="ri-heart-pulse-line text-orange-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  Whole Life Insurance
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Guaranteed cash value growth + lifetime protection. The cornerstone of any long-term wealth-building strategy.
                </p>
                <span className="inline-flex items-center text-orange-600 font-semibold text-sm">
                  Explore Whole Life <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/annuities"
              className="group bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-200 hover:border-orange-400 flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="ri-funds-box-line text-orange-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  Fixed Indexed Annuities
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Tax-deferred growth with zero market risk. Lock in gains and never lose principal — ideal for retirement income planning.
                </p>
                <span className="inline-flex items-center text-orange-600 font-semibold text-sm">
                  Explore Annuities <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/indexed-universal-life"
              className="group bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-200 hover:border-orange-400 flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="ri-line-chart-line text-orange-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  Indexed Universal Life (IUL)
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Flexible premiums + market-linked growth with downside protection. The most versatile wealth-building policy available.
                </p>
                <span className="inline-flex items-center text-orange-600 font-semibold text-sm">
                  Explore IUL <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>

            <Link
              to="/generational-wealth"
              className="group bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-all border border-gray-200 hover:border-orange-400 flex items-start gap-5"
            >
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-200 transition-colors">
                <i className="ri-parent-line text-orange-600 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  Generational Wealth Planning
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-3">
                  Leave a legacy your grandchildren will thank you for. Structured strategies to transfer wealth tax-efficiently across generations.
                </p>
                <span className="inline-flex items-center text-orange-600 font-semibold text-sm">
                  Explore Generational Wealth <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </div>
            </Link>
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/term-vs-whole-life"
              className="inline-flex items-center text-slate-700 hover:text-orange-600 font-medium transition-colors"
            >
              <i className="ri-scales-line mr-2 text-lg"></i>
              Not sure which product is right for you? Compare term vs. whole life →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Build Wealth with Life Insurance?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Schedule a free wealth strategy call to discuss your goals, review your current coverage, and create a customized plan for building lasting wealth for your Indiana family.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/wealth-strategy-call"
              className="inline-flex items-center justify-center px-8 py-4 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Book Free Strategy Call
              <i className="ri-calendar-check-line ml-2 text-xl"></i>
            </Link>
            <a
              href="tel:+13176482983"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Explore More Wealth-Building Resources</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/generational-wealth" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all border border-gray-200 hover:border-orange-400">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                <i className="ri-parent-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">Generational Wealth</h3>
              <p className="text-gray-600 text-sm mb-4">Learn how to build lasting wealth that passes to your children and grandchildren.</p>
              <div className="flex items-center text-orange-600 font-semibold text-sm">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/whole-life" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all border border-gray-200 hover:border-orange-400">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                <i className="ri-heart-pulse-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">Whole Life Insurance</h3>
              <p className="text-gray-600 text-sm mb-4">Permanent coverage with guaranteed cash value growth and tax advantages.</p>
              <div className="flex items-center text-orange-600 font-semibold text-sm">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>

            <Link to="/annuities" className="group bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all border border-gray-200 hover:border-orange-400">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                <i className="ri-funds-line text-orange-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">Annuities</h3>
              <p className="text-gray-600 text-sm mb-4">Guaranteed retirement income that protects your wealth and provides lifetime security.</p>
              <div className="flex items-center text-orange-600 font-semibold text-sm">
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Visible FAQ section — crawlable content matching FAQPage schema */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest text-orange-600 uppercase mb-3">Common Questions</span>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Wealth-Building Life Insurance — FAQ</h2>
            <p className="text-gray-600 max-w-xl mx-auto text-[15px]">Answers to the questions Indiana families ask most before starting a wealth-building strategy.</p>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-bold text-slate-900 mb-2">How can life insurance help build wealth?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Permanent life insurance policies — whole life and indexed universal life — accumulate cash value that grows tax-deferred inside the policy. You can access this cash value through policy loans or withdrawals for major expenses, retirement income, or investment opportunities, all while maintaining your death benefit protection. Unlike a 401(k) or IRA, there are no annual contribution limits on premium funding, and the death benefit creates an immediate, guaranteed wealth transfer from day one of coverage.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-bold text-slate-900 mb-2">What is the difference between term life and wealth-building life insurance?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Term life insurance provides pure death benefit protection for a specific period (10–30 years) at a low cost, with no cash value component. Wealth-building policies — whole life or indexed universal life — combine death benefit protection with a cash value account that grows over time. The cash value becomes a financial asset you can access during your lifetime: borrow against it tax-free, use it as collateral, or draw it down as tax-advantaged retirement income. Most Indiana families benefit from a combination: term for maximum coverage during peak earning years, permanent for long-term wealth accumulation.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Is life insurance cash value growth really tax-free?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Cash value growth inside a life insurance policy is tax-deferred — you don't pay taxes on gains as they accumulate each year. Policy loans are generally income tax-free as long as the policy remains in force, which is why IUL and whole life are used as tax-free retirement income vehicles. Death benefits paid to beneficiaries are also typically 100% income tax-free. Indiana has no state income tax on life insurance death benefits, making permanent policies especially attractive for Indiana families focused on wealth transfer.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-bold text-slate-900 mb-2">How much should I invest in a wealth-building life insurance policy?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">The optimal premium amount depends on your income, financial goals, age, and overall wealth strategy. Wealth-building policies work best when you can commit to consistent premiums for at least 10–15 years — the cash value compounds significantly over time. Most Indiana clients we work with fund between $300 and $2,000 per month in a permanent policy designed for cash value accumulation. We run personalized illustrations based on your age, health, and goals to show you exactly how the policy performs over 20, 30, and 40 years before you commit to anything.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-bold text-slate-900 mb-2">What is the Infinite Banking Concept and can I use it in Indiana?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">The Infinite Banking Concept (IBC) — sometimes called "being your own bank" — is a strategy where you overfund a dividend-paying whole life policy and then borrow against the cash value rather than going to a traditional bank for major purchases. The borrowed funds remain in the policy earning dividends while you use the money elsewhere. Indiana residents can absolutely implement IBC — the key is using a properly structured whole life policy with a paid-up additions rider to maximize early cash value growth. We specialize in designing these policies for Indiana clients.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h3 className="text-lg font-bold text-slate-900 mb-2">How is wealth-building life insurance different from investing in the stock market?</h3>
              <p className="text-gray-600 text-sm leading-relaxed">Stock market investments offer potentially higher long-term returns but come with volatility, market risk, and no death benefit. Wealth-building life insurance offers guaranteed or index-linked growth with principal protection, tax-deferred accumulation, tax-free access through loans, a guaranteed death benefit, and creditor protection in Indiana. The two are not either/or — most comprehensive financial plans include both. Life insurance fills the role of stable, tax-advantaged, protected wealth that complements more volatile market investments.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}